import { useState } from "react";
import { X, Search, Plus, Check, Layers } from "lucide-react";
import { ResponsiveSheet } from "./ResponsiveSheet";
import { useBurrow } from "./BurrowStore";
import { supabase } from "../lib/supabaseClient";
import { useNavigate } from "react-router";

interface ThreadPickerProps {
  open: boolean;
  onClose: () => void;
  thoughtId: string;
  /** Thread ids that already contain this card — shown as disabled/checked. */
  containingThreadIds: Set<string>;
  /** Closes the parent card sheet before navigating into the thinking space. */
  onCloseSheet?: () => void;
}

export function ThreadPicker({ open, onClose, thoughtId, containingThreadIds, onCloseSheet }: ThreadPickerProps) {
  const { state, saveThreadDraft, showToast } = useBurrow();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = state.savedThreads.filter((t) =>
    !search.trim() || t.title.toLowerCase().includes(search.toLowerCase())
  );

  // Close the picker + card sheet, then drop into the thinking space with the
  // thread loaded for editing so they can immediately keep working.
  const openInThinking = (threadId: string) => {
    onClose();
    onCloseSheet?.();
    navigate("/thinking", { state: { openThreadId: threadId } });
  };

  const handleSelect = async (thread: { id: string; title: string }) => {
    if (containingThreadIds.has(thread.id) || busy) return;
    setBusy(true);
    try {
      // Append this card to the thread's existing ordered membership.
      const { data } = await supabase
        .from("thread_thoughts")
        .select("thought_id,position")
        .eq("thread_id", thread.id)
        .order("position", { ascending: true });
      const existing = (data ?? []).map((r) => r.thought_id as string);
      if (!existing.includes(thoughtId)) {
        await saveThreadDraft(thread.id, thread.title, [...existing, thoughtId]);
        showToast(`Added to "${thread.title}".`);
      }
      openInThinking(thread.id);
    } catch {
      showToast("Failed to add to trail.");
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim() || busy) return;
    setBusy(true);
    try {
      const newId = await saveThreadDraft(null, newTitle.trim(), [thoughtId]);
      showToast(`Trail "${newTitle.trim()}" created.`);
      setNewTitle("");
      setCreating(false);
      openInThinking(newId);
    } catch {
      showToast("Failed to create trail.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      label="Add to trail"
      desktop="center"
      desktopWidth="max-w-[420px]"
      zIndex={60}
    >
            {/* Header */}
            <div className="flex items-center justify-between p-5 pb-0">
              <h2
                className="text-primary"
                style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "1.1rem", fontWeight: 400 }}
              >
                Add to trail
              </h2>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
              >
                <X size={16} />
              </button>
            </div>

            {/* Search */}
            <div className="px-5 pt-4 pb-3">
              <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-border/40 bg-background">
                <Search size={14} className="text-muted-foreground shrink-0" />
                <input
                  type="text"
                  placeholder="Search trails..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-transparent outline-none w-full text-foreground placeholder:text-muted-foreground/50"
                  style={{ fontSize: "0.85rem", fontFamily: "'Fraunces', Georgia, serif" }}
                  autoFocus
                />
              </div>
            </div>

            {/* Thread list */}
            <div className="max-h-[280px] overflow-y-auto px-3 pb-2">
              {filtered.map((thread) => {
                const alreadyIn = containingThreadIds.has(thread.id);
                return (
                  <button
                    key={thread.id}
                    onClick={() => handleSelect(thread)}
                    disabled={alreadyIn || busy}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-left ${
                      alreadyIn
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-muted/30 cursor-pointer"
                    }`}
                  >
                    <Layers size={14} className="text-accent/60 shrink-0" />
                    <span className="flex-1 min-w-0 text-foreground truncate" style={{ fontSize: "0.88rem" }}>
                      {thread.title}
                    </span>
                    {alreadyIn && <Check size={14} className="text-accent shrink-0" />}
                  </button>
                );
              })}

              {filtered.length === 0 && (
                <p className="text-center text-muted-foreground/50 py-6" style={{ fontSize: "0.85rem" }}>
                  No trails found.
                </p>
              )}
            </div>

            {/* Create new */}
            <div className="border-t border-border/30 p-4">
              {creating ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreate();
                      if (e.key === "Escape") { setCreating(false); setNewTitle(""); }
                    }}
                    placeholder="Trail title..."
                    className="flex-1 px-3 py-2 rounded-xl border border-border/40 bg-background text-foreground placeholder:text-muted-foreground/40 outline-none focus:border-accent/40"
                    style={{ fontSize: "0.85rem", fontFamily: "'Fraunces', Georgia, serif" }}
                    autoFocus
                  />
                  <button
                    onClick={handleCreate}
                    disabled={!newTitle.trim() || busy}
                    className="px-4 py-2 rounded-xl bg-accent text-accent-foreground transition-all hover:opacity-90 disabled:opacity-40"
                    style={{ fontSize: "0.82rem" }}
                  >
                    Create
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setCreating(true)}
                  className="w-full py-2.5 rounded-xl border border-dashed border-border/50 text-muted-foreground hover:text-accent hover:border-accent/40 transition-all flex items-center justify-center gap-2"
                  style={{ fontSize: "0.85rem" }}
                >
                  <Plus size={15} />
                  New Trail
                </button>
              )}
            </div>
    </ResponsiveSheet>
  );
}
